import { ShieldCheck } from "lucide-react";
import { LEGAL_VERSIONS } from "./legalConfig";
import { getSupabaseClient } from "../providers/supabase/supabase";

export interface LgpdConsentCheckboxProps {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  required?: boolean;
  purposeLabel?: string;
  version?: string;
}

/**
 * Componente reutilizável de consentimento LGPD para formulários públicos/autenticados
 * (Art. 7º, I; Art. 8º e Art. 9º da Lei 13.709/2018).
 * - Sempre inicia DESMARCADO (sem pre-ticked box).
 * - Explicita a finalidade específica e os links para a Política de Privacidade e Termos de Uso.
 */
export const LgpdConsentCheckbox = ({
  id = "lgpd-consent-checkbox",
  checked,
  onChange,
  required = true,
  purposeLabel = "criação e gestão da minha conta, autenticação de segurança e execução dos serviços contratados na plataforma Dotus CRM",
  version = LEGAL_VERSIONS.privacyPolicyVersion,
}: LgpdConsentCheckboxProps) => {
  return (
    <div className="rounded-lg border border-border/80 bg-muted/30 p-3 text-xs leading-relaxed">
      <label
        htmlFor={id}
        className="flex items-start gap-2.5 cursor-pointer select-none"
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          required={required}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        />
        <span className="text-muted-foreground">
          Declaro que li e concordo com os{" "}
          <a
            href="#/termos"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
          >
            Termos de Uso
          </a>{" "}
          e com a{" "}
          <a
            href="#/privacidade"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="font-semibold text-primary underline underline-offset-2 hover:text-primary/80"
          >
            Política de Privacidade
          </a>{" "}
          (versão <code className="text-[10px]">{version}</code>), autorizando o
          tratamento dos meus dados pessoais para a finalidade de{" "}
          <strong className="text-foreground font-medium">{purposeLabel}</strong>
          {required ? " *" : ""}.
        </span>
      </label>
      <div className="mt-1.5 pl-6 flex items-center gap-1.5 text-[10px] text-muted-foreground/80">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
        <span>
          Você pode revogar o consentimento ou exportar seus dados a qualquer
          momento em Meu Perfil (Art. 8º, § 5º e Art. 18 da LGPD).
        </span>
      </div>
    </div>
  );
};

/**
 * Persiste o comprovante de consentimento na tabela `public.consents` (via API `/api/lgpd` para capturar IP real,
 * com fallback direto via Supabase client caso esteja em modo serverless/local).
 */
export async function recordLgpdConsent(params: {
  crm_id?: string | null;
  contact_id?: number | string | null;
  subject_identifier?: string | null;
  purpose: string;
  legal_basis?:
    | "consentimento"
    | "legitimo_interesse"
    | "execucao_contrato"
    | "obrigacao_legal"
    | "protecao_credito";
  consent_text_version?: string;
  consent_text_snapshot?: string;
  granted: boolean;
  origin: string;
  metadata?: Record<string, any>;
}): Promise<void> {
  const version =
    params.consent_text_version || LEGAL_VERSIONS.privacyPolicyVersion;

  try {
    const client = getSupabaseClient();
    const { data: sessionData } = await client.auth.getSession();
    const token = sessionData?.session?.access_token;

    if (token) {
      const response = await fetch("/api/lgpd", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(params.crm_id ? { "x-crm-id": String(params.crm_id) } : {}),
        },
        body: JSON.stringify({
          action: "record_consent",
          crm_id: params.crm_id || null,
          contact_id: params.contact_id || null,
          subject_identifier: params.subject_identifier || null,
          purpose: params.purpose,
          legal_basis: params.legal_basis || "consentimento",
          consent_text_version: version,
          consent_text_snapshot: params.consent_text_snapshot || null,
          granted: params.granted,
          origin: params.origin,
          metadata: params.metadata || {},
        }),
      });

      if (response.ok) {
        return;
      }
    }

    // Fallback direto na tabela `consents` se o usuário acabou de se cadastrar
    if (sessionData?.session?.user?.id || params.crm_id) {
      await client.from("consents").insert({
        crm_id: params.crm_id || null,
        user_id: sessionData?.session?.user?.id || null,
        contact_id: params.contact_id ? Number(params.contact_id) : null,
        subject_identifier:
          params.subject_identifier ||
          sessionData?.session?.user?.email ||
          null,
        purpose: params.purpose,
        legal_basis: params.legal_basis || "consentimento",
        consent_text_version: version,
        consent_text_snapshot: params.consent_text_snapshot || null,
        granted: params.granted,
        revoked_at: params.granted ? null : new Date().toISOString(),
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : "browser",
        origin: params.origin,
        metadata: params.metadata || {},
      });
    }
  } catch (err) {
    console.warn("[LGPD] Falha não-bloqueante ao registrar consentimento:", err);
  }
}

